/**
 * UsersEditor — Sprint 33E · Feb 2026.
 *
 * CRUD for platform users (admin + supervisor; supervisors can't touch admins) (agente / supervisor / admin).
 * Replaces the "Agentes" section in Configuración. The live presence
 * / online view was moved to Supervisión ("Agentes conectados").
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Pencil, Key, CircleNotch, FloppyDisk, MagnifyingGlass, IdentificationCard, LinkSimple, ArrowClockwise } from "@phosphor-icons/react";

const ROLES = [
  { value: "admin", label_es: "Administrador", label_en: "Administrator" },
  { value: "supervisor", label_es: "Supervisor", label_en: "Supervisor" },
  { value: "bdc_agent", label_es: "Agente BDC", label_en: "BDC Agent" },
];

const BRANCHES = ["Kennedy", "Luperón", "Santiago", "Punta Cana", "RapidCentro"];

export default function UsersEditor({ lang = "es" }) {
  const T = (es, en) => (lang === "es" ? es : en);
  const isAdmin = useAuth().user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, tRes] = await Promise.all([
        api.get("/users"),
        api.get("/teams").catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data || []);
      setTeams(tRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = users
    .filter((u) => !roleFilter || u.role === roleFilter)
    .filter((u) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      return (
        (u.name || "").toLowerCase().includes(needle) ||
        (u.email || "").toLowerCase().includes(needle) ||
        (u.branch || "").toLowerCase().includes(needle)
      );
    });

  const roleLabel = (r) => {
    const found = ROLES.find((x) => x.value === r);
    return found ? (lang === "es" ? found.label_es : found.label_en) : r;
  };

  return (
    <div className="space-y-4" data-testid="users-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-7 h-8 text-xs"
            placeholder={T("Buscar por nombre, correo o sucursal", "Search name, email, branch")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="users-search"
          />
        </div>
        <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="users-role-filter">
            <SelectValue placeholder={T("Todos los roles", "All roles")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{T("Todos los roles", "All roles")}</SelectItem>
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{lang === "es" ? r.label_es : r.label_en}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {isAdmin && <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            if (!window.confirm(T(
              "Se restablecerán las contraseñas de las 6 cuentas demo (admin/supervisor/sales/bdc/service/executive) a los valores documentados. ¿Continuar?",
              "This will reset passwords of the 6 demo accounts to the documented values. Continue?"
            ))) return;
            try {
              const { data } = await api.post("/users/reset-demo-passwords");
              const okCount = (data.accounts || []).filter((a) => a.reset).length;
              toast.success(T(`${okCount} cuentas demo restablecidas.`, `${okCount} demo accounts reset.`));
            } catch (e) { toast.error(e?.response?.data?.detail || String(e)); }
          }}
          data-testid="users-reset-demos-btn"
        >
          <ArrowClockwise size={12} className="mr-1" /> {T("Restablecer demos", "Reset demos")}
        </Button>}
        <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="users-add-btn">
          <Plus size={12} className="mr-1" /> {T("Nuevo usuario", "New user")}
        </Button>
      </div>

      {/* Table */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        {loading ? (
          <div className="text-center text-xs text-muted-foreground py-8">
            <CircleNotch size={16} className="inline animate-spin mr-2" />{T("Cargando…", "Loading…")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-8">
            {T("Sin usuarios que coincidan", "No matching users")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-2.5">{T("Usuario", "User")}</th>
                <th className="text-left px-4 py-2.5">{T("Rol", "Role")}</th>
                <th className="text-left px-4 py-2.5">{T("Sucursal", "Branch")}</th>
                <th className="text-left px-4 py-2.5">{T("Equipo", "Team")}</th>
                <th className="text-left px-4 py-2.5">{T("Teléfono", "Phone")}</th>
                <th className="text-right px-4 py-2.5 pr-3">{T("Acciones", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const team = teams.find((t) => (t.agent_ids || []).includes(u.id)) || teams.find((t) => t.id === u.team_id);
                return (
                <tr key={u.id} className="border-b border-border hover:bg-muted/40" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.name || "?")[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className="uppercase text-[10px]">{roleLabel(u.role)}</Badge>
                    {u.must_change_password && (
                      <div className="text-[9px] text-amber-500 mt-1">{T("Debe cambiar clave", "Must change pwd")}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{u.branch || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{team?.name || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{u.phone || "—"}</td>
                  <td className="px-4 py-2.5">
                    {(isAdmin || u.role !== "admin") && <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditUser(u)} data-testid={`user-edit-${u.id}`}>
                        <Pencil size={11} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        title={T("Copiar enlace de invitación", "Copy invite link")}
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/users/${u.id}/invite`);
                            try { await navigator.clipboard.writeText(data.invite_url); } catch (_) {}
                            toast.success(
                              T(
                                `Link copiado. Expira: ${new Date(data.expires_at).toLocaleString()}`,
                                `Link copied. Expires: ${new Date(data.expires_at).toLocaleString()}`
                              ),
                              { description: data.invite_url, duration: 12000 }
                            );
                          } catch (e) { toast.error(e?.response?.data?.detail || String(e)); }
                        }}
                        data-testid={`user-invite-${u.id}`}
                      >
                        <LinkSimple size={11} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setResetUser(u)} data-testid={`user-reset-${u.id}`}>
                        <Key size={11} />
                      </Button>
                      {isAdmin && <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:bg-destructive/10"
                        onClick={async () => {
                          if (!window.confirm(T(`¿Eliminar a ${u.name}?`, `Delete ${u.name}?`))) return;
                          try {
                            await api.delete(`/users/${u.id}`);
                            toast.success(T("Usuario eliminado", "User deleted"));
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || String(e)); }
                        }}
                        data-testid={`user-delete-${u.id}`}
                      >
                        <Trash size={11} />
                      </Button>}
                    </div>}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {/* Create dialog */}
      {openCreate && (
        <UserDialog
          lang={lang}
          teams={teams}
          onClose={() => setOpenCreate(false)}
          onSaved={() => { setOpenCreate(false); load(); }}
        />
      )}
      {editUser && (
        <UserDialog
          lang={lang}
          teams={teams}
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
      {resetUser && (
        <ResetPasswordDialog
          lang={lang}
          user={resetUser}
          onClose={() => setResetUser(null)}
        />
      )}
    </div>
  );
}

function UserDialog({ lang, teams, user, onClose, onSaved }) {
  const T = (es, en) => (lang === "es" ? es : en);
  const isEdit = !!user;
  const isAdmin = useAuth().user?.role === "admin";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "bdc_agent",
    branch: user?.branch || "",
    phone: user?.phone || "",
    team_id: user?.team_id || "",
    must_change_password: true,
  });
  const [saving, setSaving] = useState(false);

  const canSave =
    form.name.trim().length >= 2 &&
    (isEdit || (form.email.includes("@") && form.password.length >= 8));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name,
          role: form.role,
          branch: form.branch || null,
          phone: form.phone || null,
          team_id: form.team_id || null,
        };
        await api.patch(`/users/${user.id}`, payload);
        toast.success(T("Usuario actualizado", "User updated"));
      } else {
        await api.post("/auth/register", {
          email: form.email.toLowerCase(),
          name: form.name,
          password: form.password,
          role: form.role,
          branch: form.branch || null,
          phone: form.phone || null,
          team_id: form.team_id || null,
          must_change_password: form.must_change_password,
        });
        toast.success(T("Usuario creado", "User created"));
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdentificationCard size={16} />
            {isEdit ? T(`Editar · ${user.name}`, `Edit · ${user.name}`) : T("Nuevo usuario", "New user")}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? T("Modifica datos del usuario. El correo no puede cambiarse.", "Edit user data. Email cannot be changed.")
              : T("Crea una cuenta con contraseña temporal. Se pedirá cambiarla en el primer acceso.",
                  "Create an account with a temporary password. User will be forced to change it on first login.")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{T("Nombre completo", "Full name")}</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</label>
            <Input
              type="email"
              value={form.email}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-form-email"
            />
            {isEdit && <div className="text-[10px] text-muted-foreground mt-0.5">{T("Inmutable", "Immutable")}</div>}
          </div>
          {!isEdit && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {T("Contraseña temporal (mín 8)", "Temp password (min 8)")}
              </label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-form-password"
                placeholder={T("Ej: Bienvenido2026!", "e.g. Welcome2026!")}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{T("Rol", "Role")}</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.filter((r) => isAdmin || r.value !== "admin").map((r) => (
                    <SelectItem key={r.value} value={r.value}>{lang === "es" ? r.label_es : r.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{T("Sucursal", "Branch")}</label>
              <Select value={form.branch || "none"} onValueChange={(v) => setForm({ ...form, branch: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-form-branch"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {BRANCHES.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{T("Teléfono", "Phone")}</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-form-phone" placeholder="+18091234567" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{T("Equipo", "Team")}</label>
              <Select value={form.team_id || "none"} onValueChange={(v) => setForm({ ...form, team_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-form-team"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {teams.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{T("Cancelar", "Cancel")}</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="user-form-save">
            {saving ? <CircleNotch size={12} className="animate-spin mr-1" /> : <FloppyDisk size={12} className="mr-1" />}
            {isEdit ? T("Guardar", "Save") : T("Crear usuario", "Create user")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ lang, user, onClose }) {
  const T = (es, en) => (lang === "es" ? es : en);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      toast.success(T("Contraseña restablecida. El usuario deberá cambiarla al ingresar.", "Password reset. User must change it on next login."));
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="user-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Key size={16} /> {T("Restablecer contraseña", "Reset password")}</DialogTitle>
          <DialogDescription>{user.name} · {user.email}</DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {T("Nueva contraseña temporal (mín 8)", "New temp password (min 8)")}
          </label>
          <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="user-reset-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{T("Cancelar", "Cancel")}</Button>
          <Button onClick={doReset} disabled={pw.length < 8 || busy} data-testid="user-reset-save">
            {busy ? <CircleNotch size={12} className="animate-spin mr-1" /> : <Key size={12} className="mr-1" />}
            {T("Restablecer", "Reset")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
